import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Eye, Filter, AArrowDown, AArrowUp } from "lucide-react";
import { getAllShipmentDetail, retryFailedShipmentTracking } from "../../reducers/shipment.slice";
import {
  convertDateFormatInUtc,
  countActiveFiltersAndSort,
  getShipmentDeadlineInfo,
} from "../../utils/util";
import { getUsers } from "../../utils/getUser";
import { AuthContext } from "../../context/authContext";
import { FileUploadLogo } from "../../assests/logo/fileUploadLogo";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import DataTable from "../../components/shared/DataTable";
import PageHeader from "../../components/shared/PageHeader";
import SearchBar from "../../components/shared/SearchBar";
import RefreshButton from "../../components/shared/RefreshButton";
import StatusBadge from "../../components/shared/StatusBadge";
import useMediaQuery from "../../components/shared/useMediaQuery";
import CreateShipmentPage from "./CreateShipmentPage";
import UploadShipmentReceiptPage from "./UploadRecieptPage";
import UploadShipmentLabelPage from "./UploadShipmentLabelPage";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
} from "../../components/ui/dropdown-menu";

const SHIPMENT_STATUSES = ["LABEL_PENDING", "PENDING_SHIPMENT", "SHIPPED", "IN_TRANSIT", "RECEIVED", "DELAYED", "CANCELLED"];
const SHIPMENT_TYPES = ["VENDOR", "SHIP_DIRECT"];
const CHUNK_SIZE = 50;

const SHIPMENT_DEADLINE_TITLE =
  "Ship within 48h of receiving labels. Shipments not scanned in time may be cancelled.";

const truncate = (val, max) => {
  if (!val) return "—";
  const s = String(val);
  return s.length > max ? s.slice(0, max) + "…" : s;
};

const statStyle = (accent, isMobile) => ({
  background: "linear-gradient(180deg, rgba(255,255,255,0.025), transparent)",
  border: "1px solid rgba(255,255,255,0.06)",
  borderTop: `3px solid ${accent}`,
  borderRadius: isMobile ? 10 : 14,
  padding: isMobile ? "10px 12px" : "16px 18px",
  minWidth: 0,
});

const statLabelStyle = (isMobile) => ({
  display: "block",
  color: "#c3c8cc",
  fontSize: isMobile ? 9 : 10.5,
  fontWeight: 800,
  letterSpacing: "0.08em",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const statValueStyle = (isMobile) => ({
  display: "block",
  color: "#fff",
  fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
  fontSize: isMobile ? 18 : 24,
  fontWeight: 800,
  marginTop: isMobile ? 4 : 8,
});

const ShippingAndTrackingPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = getUsers();
  const { showToast } = useContext(AuthContext);

  const [showLoader, setShowLoader] = useState(false);
  const [allLoadedData, setAllLoadedData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const s = sessionStorage.getItem("shipTracking_selectedStatuses"); return s ? JSON.parse(s) : [];
  });
  const [selectedTypes, setSelectedTypes] = useState(() => {
    const s = sessionStorage.getItem("shipTracking_selectedTypes"); return s ? JSON.parse(s) : [];
  });
  const [sortDirection, setSortDirection] = useState(() => sessionStorage.getItem("shipTracking_sortDirection") || null);
  const [prevSortDirection, setPrevSortDirection] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [loadingChunk, setLoadingChunk] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const s = sessionStorage.getItem("shipTracking_rowsPerPage"); return s ? JSON.parse(s) : 5;
  });
  const [totalRows, setTotalRows] = useState(0);
  const [isNavigatingToPage, setIsNavigatingToPage] = useState(false);
  const [pageCache, setPageCache] = useState(new Map());
  const [currentChunk, setCurrentChunk] = useState(0);
  const [receiptShipmentId, setReceiptShipmentId] = useState(null);
  const [labelShipmentId, setLabelShipmentId] = useState(null);
  const [createShipmentOpen, setCreateShipmentOpen] = useState(false);
  const isInitialRender = useRef(true);
  const isMobile = useMediaQuery("(max-width: 640px)");
  const [isRetrying, setIsRetrying] = useState(false);

  const loadDataChunk = async (chunkNumber = 0) => {
    const filterKey = JSON.stringify({
      selectedStatuses: [...selectedStatuses].sort(),
      selectedTypes: [...selectedTypes].sort(),
      search: searchTerm,
      sortDirection,
    });
    const cacheKey = `chunk-${chunkNumber}-${filterKey}`;
    if (pageCache.has(cacheKey)) {
      const cached = pageCache.get(cacheKey);
      if (cached.totalRows !== undefined) setTotalRows(cached.totalRows);
      return cached.data;
    }
    if (loadingChunk) return [];
    setLoadingChunk(true);
    if (chunkNumber === 0 && allLoadedData.length === 0) setShowLoader(true);
    try {
      const payload = {
        skip: chunkNumber * CHUNK_SIZE,
        limit: CHUNK_SIZE,
        shipment_status: selectedStatuses.length > 0 ? selectedStatuses : [],
        shipment_type: selectedTypes.length > 0 ? selectedTypes : [],
      };
      if (searchTerm?.trim()) payload.search = searchTerm.trim();
      if (sortDirection && sortDirection !== "null") { payload.sort_by = "TOTAL_BOXES"; payload.sort_order = sortDirection; }
      const res = await dispatch(getAllShipmentDetail(payload));
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error"); setHasMoreData(false); setFilteredData([]); return [];
      }
      if (!res?.payload?.data || !Array.isArray(res.payload.data)) { setHasMoreData(false); setFilteredData([]); return []; }
      if ((chunkNumber === 0 || !totalRows) && res?.payload?.total) setTotalRows(res.payload.total);
      const formatted = res.payload.data.map((item) => {
        const shipDeadlineInfo = getShipmentDeadlineInfo(item);

        return {
          ...item,
          shipmentDate: item?.shipment_date ? convertDateFormatInUtc(item.shipment_date, "MMMM D, YYYY") : null,
          labelUploadedDate: item?.label_upload_date
            ? convertDateFormatInUtc(item.label_upload_date, "MMM D, YYYY · h:mm A")
            : "—",
          shipByDate: shipDeadlineInfo
            ? convertDateFormatInUtc(shipDeadlineInfo.deadline, "MMM D, YYYY · h:mm A")
            : "—",
          shipDeadlineInfo,
          member_name: item?.user_details?.first_name + " " + item?.user_details?.last_name,
          displayStatus:
            item?.status === "LABEL_PENDING" ? "Label Pending"
            : item?.status === "PENDING_SHIPMENT" ? "Shipment Pending"
            : item?.status === "SHIPPED" ? "Shipped"
            : item?.status === "IN_TRANSIT" ? "In-Transit"
            : item?.status === "RECEIVED" ? "Received"
            : item?.status === "DELAYED" ? "Delayed"
            : item?.status === "CANCELLED" ? "Cancelled"
            : "Unknown Status",
        };
      });
      setPageCache((prev) => new Map(prev.set(cacheKey, { data: formatted, timestamp: Date.now(), totalRows: res.payload.total })));
      setHasMoreData(formatted.length === CHUNK_SIZE);
      return formatted;
    } catch (error) {
      showToast("Error fetching data", "error"); setHasMoreData(false); return [];
    } finally {
      setLoadingChunk(false); setShowLoader(false);
    }
  };

  const loadInitialData = async () => {
    const chunk = await loadDataChunk(0);
    setAllLoadedData(chunk); setCurrentChunk(0);
  };

  const checkAndLoadMoreData = async (page, rpp) => {
    const required = (page + 1) * rpp;
    if (allLoadedData.length >= required || allLoadedData.length >= totalRows || !hasMoreData || loadingChunk) return;
    try {
      setIsNavigatingToPage(true);
      const nextChunk = Math.floor(allLoadedData.length / CHUNK_SIZE);
      const newData = await loadDataChunk(nextChunk);
      if (newData?.length > 0) {
        await new Promise((resolve) => {
          setAllLoadedData((prev) => { const updated = [...prev, ...newData]; resolve(updated); return updated; });
        });
        const pageData = [...allLoadedData, ...newData].slice(page * rpp, page * rpp + rpp);
        setCurrentPage(page); setFilteredData(pageData);
      }
    } catch (error) { showToast("Error loading data", "error"); }
    finally { setIsNavigatingToPage(false); }
  };

  const handleChangePage = async (newPage) => {
    if (isNavigatingToPage || loadingChunk) return;
    const required = (newPage + 1) * rowsPerPage;
    try {
      setIsNavigatingToPage(true);
      if (allLoadedData.length < required && hasMoreData) { await checkAndLoadMoreData(newPage, rowsPerPage); return; }
      setCurrentPage(newPage);
      setFilteredData(allLoadedData.slice(newPage * rowsPerPage, newPage * rowsPerPage + rowsPerPage));
    } catch (error) { showToast("Error loading page data", "error"); }
    finally { setIsNavigatingToPage(false); }
  };

  const handleChangeRowsPerPage = async (n) => {
    setRowsPerPage(n); sessionStorage.setItem("shipTracking_rowsPerPage", JSON.stringify(n)); setCurrentPage(0);
    if (allLoadedData.length < n && hasMoreData && !loadingChunk) checkAndLoadMoreData(0, n);
  };

  const applyFilters = async () => {
    try {
      setLoadingChunk(true); setCurrentPage(0); setAllLoadedData([]);
      sessionStorage.setItem("shipTracking_selectedStatuses", JSON.stringify(selectedStatuses));
      sessionStorage.setItem("shipTracking_selectedTypes", JSON.stringify(selectedTypes));
      sessionStorage.setItem("shipTracking_sortDirection", sortDirection);
      const results = await loadDataChunk(0);
      if (results?.length > 0) { setFilteredData(results.slice(0, rowsPerPage)); setAllLoadedData(results); }
      else { setFilteredData([]); setAllLoadedData([]); }
    } catch (error) { showToast("Error applying filters", "error"); }
    finally { setLoadingChunk(false); }
  };

  const handleRefresh = () => {
    const currentPageBeforeRefresh = currentPage;
    setPageCache(new Map()); setAllLoadedData([]); setHasMoreData(true);
    loadInitialData().then(() => {
      if (currentPageBeforeRefresh > 0) setTimeout(() => setCurrentPage(currentPageBeforeRefresh), 100);
    });
  };

  const handleRetryShipments = async () => {
    if (isRetrying) return;
    setIsRetrying(true);
    try {
      const result = await dispatch(retryFailedShipmentTracking());
      if (result?.meta?.requestStatus === "fulfilled") {
        showToast("Shipment tracking retry completed successfully.", "success");
        handleRefresh();
      } else {
        const errorMessage = typeof result?.payload === "string"
          ? result.payload
          : result?.payload?.message || result?.error?.message || "Failed to retry shipment tracking.";
        showToast(errorMessage, "error");
      }
    } catch (error) {
      showToast(error?.message || "Failed to retry shipment tracking.", "error");
    } finally {
      setIsRetrying(false);
    }
  };

  const handleViewShipment = (row) => {
    sessionStorage.removeItem("updatedQuantities");
    sessionStorage.removeItem("trackingNumbers");
    sessionStorage.removeItem("ship_to_address");
    sessionStorage.removeItem("editedQuantity");
    navigate("/dashboard/shipment/view-shipment", { state: { shipmentId: row?.id } });
  };

  useEffect(() => { loadInitialData(); }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    if (isInitialRender.current) { isInitialRender.current = false; return; }
    if (isSearching) return;
    const run = async () => {
      setIsSearching(true); setCurrentPage(0); setAllLoadedData([]);
      const results = await loadDataChunk(0);
      if (results?.length > 0) setAllLoadedData(results); else { setFilteredData([]); setAllLoadedData([]); }
      setIsSearching(false);
    };
    run();
  }, [debouncedSearch]);

  useEffect(() => {
    if (isNavigatingToPage || loadingChunk || !allLoadedData?.length) return;
    const pageData = allLoadedData.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);
    if (pageData.length > 0) setFilteredData(pageData);
    else if (currentPage > 0) setCurrentPage((p) => p - 1);
  }, [rowsPerPage, allLoadedData]);

  const activeFilterCount = countActiveFiltersAndSort(sortDirection, selectedStatuses, selectedTypes);

  const shipStats = useMemo(() => {
    const IN_PROGRESS_STATUSES = ["LABEL_PENDING", "PENDING_SHIPMENT"];
    const IN_TRANSIT_STATUSES = ["SHIPPED", "IN_TRANSIT"];
    return {
      inProgress: allLoadedData.filter((d) => IN_PROGRESS_STATUSES.includes(d.status)).length,
      inTransit: allLoadedData.filter((d) => IN_TRANSIT_STATUSES.includes(d.status)).length,
      received: allLoadedData.filter((d) => d.status === "RECEIVED").length,
      issues: allLoadedData.filter((d) => ["DELAYED", "CANCELLED"].includes(d.status)).length,
    };
  }, [allLoadedData]);

  const columns = [
    {
      key: "id",
      label: "Shipment ID",
      width: 130,
      render: (row) => (
        <span title={String(row?.id ?? "")} style={{ fontSize: 13.5, fontWeight: 700, color: "#fff", whiteSpace: "nowrap" }}>
          {row?.id}
        </span>
      ),
    },
    {
      key: "shipment_type",
      label: "Shipment Type",
      width: 105,
      render: (row) => <span>{row?.shipment_type?.replace("_", " ")}</span>,
    },
    {
      key: "member_name",
      label: "Member Name",
      width: 120,
      render: (row) => <span title={row?.member_name ?? ""}>{truncate(row?.member_name, 16)}</span>,
    },
    { key: "total_boxes", label: "Total Boxes", width: 90, align: "center" },
    { key: "total_items", label: "SKU", width: 60, align: "center" },
    {
      key: "displayStatus",
      label: "Status",
      width: 120,
      align: "center",
      render: (row) => <StatusBadge status={row?.status} />,
    },
    {
      key: "shipByDate",
      label: "Shipping Deadline",
      width: 165,
      render: (row) =>
        row.shipDeadlineInfo ? (
          <span
            title={SHIPMENT_DEADLINE_TITLE}
            style={{
              color: row.shipDeadlineInfo.isPastDue ? "#f27059" : "#fff",
              fontSize: 12,
              fontWeight: row.shipDeadlineInfo.isPastDue ? 700 : 400,
            }}
          >
            {row.shipByDate}
          </span>
        ) : (
          <span style={{ color: "#8b9296" }}>—</span>
        ),
    },
    {
      key: "tracking_number",
      label: "Tracking Number",
      width: 130,
      render: (row) => <span title={row?.tracking_number ?? ""}>{truncate(row?.tracking_number, 15)}</span>,
    },
    { key: "shipmentDate", label: "Shipment Date", width: 120 },
    {
      key: "action",
      label: "Action",
      width: 100,
      align: "center",
      render: (row) => (
        <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "center" }}>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={(e) => { e.stopPropagation(); handleViewShipment(row); }}
            title="View Details"
            style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
          >
            <Eye size={15} />
          </Button>
          {user.role === "MEMBER" && row?.status === "PENDING_SHIPMENT" && row?.shipment_type !== "SHIP_DIRECT" ? (
            <Button
              variant="ghost"
              size="icon-sm"
              title="Upload Receipt"
              onClick={(e) => { e.stopPropagation(); setReceiptShipmentId(row?.id); }}
              style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
            >
              <FileUploadLogo />
            </Button>
          ) : (
            user.role !== "MEMBER" && row?.status === "LABEL_PENDING" && (
              <Button
                variant="ghost"
                size="icon-sm"
                title="Upload Label"
                onClick={(e) => { e.stopPropagation(); setLabelShipmentId(row?.id); }}
                style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
              >
                <FileUploadLogo />
              </Button>
            )
          )}
        </div>
      ),
    },
  ];

  const retryTrackingButton =
    user?.role === "SUPERADMIN" ? (
      isMobile ? (
        <Button disabled={isRetrying} onClick={handleRetryShipments} size="default" style={{ width: "100%", flex: "none", height: 38, fontSize: 12, padding: "0 16px", display: "flex", alignItems: "center", justifyContent: "center", boxSizing: "border-box", whiteSpace: "nowrap" }}>
          {isRetrying ? "Retrying..." : "Retry Tracking"}
        </Button>
      ) : (
        <Button variant="outline" disabled={isRetrying} onClick={handleRetryShipments} size="sm">
          {isRetrying ? "Retrying..." : "Retry Failed Shipments Tracking"}
        </Button>
      )
    ) : null;

  const createShipmentButton =
    user?.role === "MEMBER" ? (
      <Button
        onClick={() => setCreateShipmentOpen(true)}
        style={isMobile ? { flex: 1.2, height: 38, whiteSpace: "nowrap" } : undefined}
      >
        {isMobile ? "New Shipment" : "Create New Shipment"}
      </Button>
    ) : null;

  const filterAndSortMenu = (
    <DropdownMenu onOpenChange={(open) => { if (!open) applyFilters(); }}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="default" style={{ width: isMobile ? "100%" : "auto" }}>
          <Filter size={14} /> Filter & Sort
          {activeFilterCount > 0 && (
            <span
              style={{
                marginLeft: 4,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 16,
                height: 16,
                borderRadius: 8,
                background: "#00ADB5",
                color: "#000",
                fontSize: 10,
                fontWeight: 800,
                padding: "0 4px",
              }}
            >
              {activeFilterCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" style={{ minWidth: 240 }}>
        <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {SHIPMENT_STATUSES.map((s) => (
          <DropdownMenuCheckboxItem
            key={s}
            checked={selectedStatuses.includes(s)}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={(checked) =>
              setSelectedStatuses((prev) => (checked ? [...prev, s] : prev.filter((x) => x !== s)))
            }
          >
            {s.replace(/_/g, " ")}
          </DropdownMenuCheckboxItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Filter by Type</DropdownMenuLabel>
        {SHIPMENT_TYPES.map((t) => (
          <DropdownMenuCheckboxItem
            key={t}
            checked={selectedTypes.includes(t)}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={(checked) =>
              setSelectedTypes((prev) => (checked ? [...prev, t] : prev.filter((x) => x !== t)))
            }
          >
            {t.replace(/_/g, " ")}
          </DropdownMenuCheckboxItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Sort by Total Boxes</DropdownMenuLabel>
        <DropdownMenuItem active={sortDirection === "ASC"} onClick={() => { setPrevSortDirection(sortDirection); setSortDirection((prev) => prev === "ASC" ? null : "ASC"); }}>
          <AArrowUp size={14} style={{ marginRight: 6 }} /> Low to High
        </DropdownMenuItem>
        <DropdownMenuItem active={sortDirection === "DESC"} onClick={() => { setPrevSortDirection(sortDirection); setSortDirection((prev) => prev === "DESC" ? null : "DESC"); }}>
          <AArrowDown size={14} style={{ marginRight: 6 }} /> High to Low
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", width: "100%" }}>
      <div style={{ marginBottom: 16 }}>
        <PageHeader
          title="Shipping & Tracking"
          subtitle={
            user?.role === "MEMBER"
              ? "Review your submitted shipment details."
              : "Download receipts and manually update tracking numbers for shipments."
          }
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))",
          gap: isMobile ? 8 : 14,
          marginBottom: 20,
        }}
      >
        <div style={statStyle("#e8b84b", isMobile)}>
          <span style={statLabelStyle(isMobile)}>IN PROGRESS</span>
          <span style={statValueStyle(isMobile)}>{shipStats.inProgress}</span>
        </div>
        <div style={statStyle("#00ADB5", isMobile)}>
          <span style={statLabelStyle(isMobile)}>IN TRANSIT</span>
          <span style={statValueStyle(isMobile)}>{shipStats.inTransit}</span>
        </div>
        <div style={statStyle("#4caf50", isMobile)}>
          <span style={statLabelStyle(isMobile)}>RECEIVED</span>
          <span style={statValueStyle(isMobile)}>{shipStats.received}</span>
        </div>
        <div style={statStyle("#f27059", isMobile)}>
          <span style={statLabelStyle(isMobile)}>ISSUES</span>
          <span style={statValueStyle(isMobile)}>{shipStats.issues}</span>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "stretch" : "center",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", width: isMobile ? "100%" : "auto" }}>
          {retryTrackingButton}
          {!isMobile && <RefreshButton onRefresh={handleRefresh} spinning={showLoader || loadingChunk} />}
          {createShipmentButton}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
            width: isMobile ? "100%" : "auto",
          }}
        >
          {isMobile && <RefreshButton onRefresh={handleRefresh} spinning={showLoader || loadingChunk} />}
          <div style={{ flex: isMobile ? 1 : "none", minWidth: isMobile ? 0 : 200 }}>
            <SearchBar placeholder="Search shipments…" searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          </div>
          {filterAndSortMenu}
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={filteredData}
        totalRows={totalRows}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        loading={loadingChunk || isNavigatingToPage}
        minWidth={1120}
      />
      <Dialog open={Boolean(receiptShipmentId)} onOpenChange={(open) => !open && setReceiptShipmentId(null)}>
        <DialogContent style={{ maxWidth: 620, width: isMobile ? "calc(100vw - 16px)" : "90vw", maxHeight: "92vh" }}>
          <DialogHeader>
            <DialogTitle>Upload Shipment Receipt</DialogTitle>
            <DialogDescription>{receiptShipmentId ? `Shipment ID: ${receiptShipmentId}` : "Upload shipment receipt files."}</DialogDescription>
          </DialogHeader>
          {receiptShipmentId && (
            <UploadShipmentReceiptPage
              dialogMode
              shipmentId={receiptShipmentId}
              onCancel={() => setReceiptShipmentId(null)}
              onSuccess={() => {
                setReceiptShipmentId(null);
                handleRefresh();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(labelShipmentId)} onOpenChange={(open) => !open && setLabelShipmentId(null)}>
        <DialogContent style={{ maxWidth: 620, width: isMobile ? "calc(100vw - 16px)" : "90vw", maxHeight: "92vh" }}>
          <DialogHeader>
            <DialogTitle>Upload Shipment Label</DialogTitle>
            <DialogDescription>{labelShipmentId ? `Shipment ID: ${labelShipmentId}` : "Upload shipment label files and tracking details."}</DialogDescription>
          </DialogHeader>
          {labelShipmentId && (
            <UploadShipmentLabelPage
              dialogMode
              shipmentId={labelShipmentId}
              onCancel={() => setLabelShipmentId(null)}
              onSuccess={() => {
                setLabelShipmentId(null);
                handleRefresh();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={createShipmentOpen} onOpenChange={(open) => !open && setCreateShipmentOpen(false)}>
        <DialogContent style={{ maxWidth: 760, width: isMobile ? "calc(100vw - 16px)" : "90vw", maxHeight: "92vh" }}>
          <DialogHeader>
            <DialogTitle>Create Shipment</DialogTitle>
            <DialogDescription>Create a shipment from approved order items.</DialogDescription>
          </DialogHeader>
          {createShipmentOpen && (
            <CreateShipmentPage
              dialogMode
              onCancel={() => setCreateShipmentOpen(false)}
              onSuccess={() => {
                setCreateShipmentOpen(false);
                handleRefresh();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShippingAndTrackingPage;
